import { EkranBledu } from '@insolitum/ui';
import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, Outlet, useParams, Link, useLocation } from 'react-router-dom';

import { supabase } from '@/lib/supabase';

import { MaintenanceGate } from './components/MaintenanceGate';
import { EkranStartuPowloki, ProtectedRoute } from './components/ProtectedRoute';
import { StrazSprzedazySamoobslugowej } from './components/StrazSprzedazySamoobslugowej';
import { getAppMode } from './hooks/useAppMode';
import { AdminLayout } from './layouts/AdminLayout';
import { PartnerLayout } from './layouts/PartnerLayout';
import { SuperAdminLayout } from './layouts/SuperAdminLayout';
import { ukryjSprzedazSamoobslugowa } from './lib/fazaSprzedazy';
import { MODULE_REGISTRY } from './modules/registry';
import { moduleMaWlasnyEkran } from './modules/rodzajModulu';
import { NieZnalezionoPage } from './pages/NieZnalezionoPage';
import { OfertaPublicznaPage } from './pages/OfertaPublicznaPage';
import { UnauthorizedPage } from './pages/UnauthorizedPage';
import { AdminCategoriesPage } from './pages/admin/AdminCategoriesPage';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { AdminEquipmentPage } from './pages/admin/AdminEquipmentPage';
import { AdminProductsPage } from './pages/admin/AdminProductsPage';
import { AdminSuppliersPage } from './pages/admin/AdminSuppliersPage';
import { AdminTechniciansPage } from './pages/admin/AdminTechniciansPage';
import { AkademiaUzytkownikaPage } from './pages/admin/AkademiaUzytkownikaPage';
import { BillingPage } from './pages/admin/BillingPage';
import { CompanyProfilePage } from './pages/admin/CompanyProfilePage';
import { DevicesPage } from './pages/admin/DevicesPage';
import { ModulesPage } from './pages/admin/ModulesPage';
import { SettingsPage } from './pages/admin/SettingsPage';
import { SitesPage } from './pages/admin/SitesPage';
import { SupportPage } from './pages/admin/SupportPage';
import { UcsModulePage } from './pages/admin/UcsModulePage';
import { UsersPage } from './pages/admin/UsersPage';
import { WorkerDossierPage } from './pages/admin/WorkerDossierPage';
import { WorkersPage } from './pages/admin/WorkersPage';
import { ObiegDokumentowPage } from './pages/admin/obieg/ObiegDokumentowPage';
import { PrintingPage } from './pages/admin/printing/PrintingPage';
import { AcceptInvitationPage } from './pages/auth/AcceptInvitationPage';
import { DwuskladnikowePage } from './pages/auth/DwuskladnikowePage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { LoginPage } from './pages/auth/LoginPage';
import { PartnerRegisterPage } from './pages/auth/PartnerRegisterPage';
import { RegisterPage } from './pages/auth/RegisterPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { useAuth } from './providers/AuthProvider';
// FEDERATED MODULES - Loaded via Module Federation at runtime
import { BuiltInModuleRoute, DynamicModuleFrame, ModuleLoadingFallback } from './utils/federation';

// =============================================================================
// CODE-SPLITTING — heavy panels load on demand instead of in the entry bundle.
// Each barrel import below becomes one chunk (super-admin, partner, admin/nexus,
// mobile, three.js scenes), so a tenant admin no longer downloads the whole
// super-admin cockpit (THREE.js, Cosmograph) just to see /login.
// =============================================================================
type PageModule<T> = () => Promise<T>;
function lazyPage<T extends Record<string, React.ComponentType<never>>, K extends keyof T>(
  loader: PageModule<T>,
  name: K
) {
  return lazy(() => loader().then((m) => ({ default: m[name] as React.ComponentType })));
}

const superAdminPages = () => import('./pages/super-admin');
const SuperAdminDashboard = lazyPage(superAdminPages, 'SuperAdminDashboard');
const SuperAdminLandingPage = lazyPage(superAdminPages, 'SuperAdminLandingPage');
const TenantsPage = lazyPage(superAdminPages, 'TenantsPage');
const TenantAnalyticsPage = lazyPage(superAdminPages, 'TenantAnalyticsPage');
const PartnersPage = lazyPage(superAdminPages, 'PartnersPage');
const PartnerSettlementsPage = lazyPage(superAdminPages, 'PartnerSettlementsPage');
const PartnerMailboxesPage = lazyPage(superAdminPages, 'PartnerMailboxesPage');
const PartnerCertyfikatyPage = lazyPage(superAdminPages, 'PartnerCertyfikatyPage');
const KlienciReceptoraPage = lazyPage(superAdminPages, 'KlienciReceptoraPage');
const LicensesPage = lazyPage(superAdminPages, 'LicensesPage');
const PairingsPage = lazyPage(superAdminPages, 'PairingsPage');
const FlotaPaneliPage = lazyPage(superAdminPages, 'FlotaPaneliPage');
const EmailConfigPage = lazyPage(superAdminPages, 'EmailConfigPage');
const EmailTemplatesPage = lazyPage(superAdminPages, 'EmailTemplatesPage');
const SuperAdminSettingsPage = lazyPage(superAdminPages, 'SuperAdminSettingsPage');
const SimulationsPage = lazyPage(superAdminPages, 'SimulationsPage');
const ModuleReviewPage = lazyPage(superAdminPages, 'ModuleReviewPage');
const ModulePricingPage = lazyPage(superAdminPages, 'ModulePricingPage');
const KatalogSprzetuPage = lazyPage(superAdminPages, 'KatalogSprzetuPage');
const KartySprzedazowePage = lazyPage(superAdminPages, 'KartySprzedazowePage');
const PlanPricingPage = lazyPage(superAdminPages, 'PlanPricingPage');
const MobilePricingPage = lazyPage(superAdminPages, 'MobilePricingPage');
const SubscriptionsPage = lazyPage(superAdminPages, 'SubscriptionsPage');
const NexusSuperAdminPage = lazyPage(superAdminPages, 'NexusSuperAdminPage');
const NexusProposalsPage = lazyPage(superAdminPages, 'NexusProposalsPage');
const HealthIncidentsPage = lazyPage(superAdminPages, 'HealthIncidentsPage');
const SteeringOpportunitiesPage = lazyPage(superAdminPages, 'SteeringOpportunitiesPage');
const ServiceHealthPage = lazyPage(superAdminPages, 'ServiceHealthPage');
const ModulesManagementPage = lazyPage(superAdminPages, 'ModulesManagementPage');
const AuditLogPage = lazyPage(superAdminPages, 'AuditLogPage');
const NexusAssistantDashboardPage = lazyPage(superAdminPages, 'NexusAssistantDashboardPage');
const NexusAssistantUpdatesPage = lazyPage(superAdminPages, 'NexusAssistantUpdatesPage');
const OmniVoiceUpdatesPage = lazyPage(superAdminPages, 'OmniVoiceUpdatesPage');
const SuperNexusPage = lazyPage(superAdminPages, 'SuperNexusPage');
const PlatformLivingNetworkPage = lazyPage(superAdminPages, 'PlatformLivingNetworkPage');
const CockpitPage = lazyPage(superAdminPages, 'CockpitPage');
const CodeTasksPage = lazyPage(superAdminPages, 'CodeTasksPage');
const GenesisConnectorsPage = lazyPage(superAdminPages, 'GenesisConnectorsPage');

const partnerPages = () => import('./pages/partner');
const PartnerDashboard = lazyPage(partnerPages, 'PartnerDashboard');
const PartnerClientsPage = lazyPage(partnerPages, 'PartnerClientsPage');
const PartnerClientAnalyticsPage = lazyPage(partnerPages, 'PartnerClientAnalyticsPage');
const PartnerCommissionsPage = lazyPage(partnerPages, 'PartnerCommissionsPage');
const PartnerReportsPage = lazyPage(partnerPages, 'PartnerReportsPage');
const PartnerProfilePage = lazyPage(partnerPages, 'PartnerProfilePage');
const PartnerSettingsPage = lazyPage(partnerPages, 'PartnerSettingsPage');
const PartnerLandingPage = lazyPage(partnerPages, 'PartnerLandingPage');
const PartnerModulesPage = lazyPage(partnerPages, 'PartnerModulesPage');
const PartnerTeamPage = lazyPage(partnerPages, 'PartnerTeamPage');
const PartnerOfferPricingPage = lazyPage(partnerPages, 'PartnerOfferPricingPage');
const PartnerOfertaPodgladPage = lazyPage(partnerPages, 'PartnerOfertaPodgladPage');
const PartnerTerritoryPage = lazyPage(partnerPages, 'PartnerTerritoryPage');
const AkademiaPage = lazyPage(partnerPages, 'AkademiaPage');
const CertyfikatyPage = lazyPage(partnerPages, 'CertyfikatyPage');
const MojePremiePage = lazyPage(partnerPages, 'MojePremiePage');

const nexusModulePages = () => import('./pages/admin/nexus');
const NexusModuleLayout = lazyPage(nexusModulePages, 'NexusModuleLayout');
const NexusOverviewPage = lazyPage(nexusModulePages, 'NexusOverviewPage');
const NexusBillingPage = lazyPage(nexusModulePages, 'NexusBillingPage');
const NexusSettingsPage = lazyPage(nexusModulePages, 'NexusSettingsPage');
const NexusChannelsPage = lazyPage(nexusModulePages, 'NexusChannelsPage');

const EarthLandingPage = lazyPage(
  () => import('./pages/dashboard/EarthLandingPage'),
  'EarthLandingPage'
);
const JarvisDashboardPage = lazyPage(
  () => import('./pages/admin/JarvisDashboardPage'),
  'JarvisDashboardPage'
);
const LivingNetworkPage = lazyPage(
  () => import('./pages/admin/LivingNetworkPage'),
  'LivingNetworkPage'
);
const MobileApp = lazyPage(() => import('./pages/mobile/MobileApp'), 'MobileApp');
const KioskApp = lazyPage(() => import('./pages/mobile/kiosk'), 'KioskApp');
const NexusOnboardingPage = lazy(() => import('./pages/onboarding/NexusOnboardingPage'));
const TermsPage = lazyPage(() => import('./pages/legal/TermsPage'), 'TermsPage');
const PrivacyPage = lazyPage(() => import('./pages/legal/PrivacyPage'), 'PrivacyPage');

// =============================================================================
// MODULE LAYOUTS & PLACEHOLDERS
// =============================================================================

// Simple layout that just renders children/outlet
const ModuleLayout = () => <Outlet />;

// Mobile placeholders (will be separate PWA apps).
// An honest "in preparation" screen — the previous ModuleLoadingFallback spun
// forever, looking like a hung app instead of an unfinished feature.
const MobileLayout = () => <Outlet />;
function FeatureInPreparation({ name }: { name: string }) {
  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 p-6">
      <div className="text-center p-8 bg-white/5 backdrop-blur-xl rounded-ids-xl border border-white/10 max-w-md">
        <h2 className="text-xl font-bold text-aurora-on-accent mb-2">{name}</h2>
        <p className="text-gray-400 mb-6">
          Ta funkcja jest w przygotowaniu. Skorzystaj z uniwersalnej aplikacji mobilnej.
        </p>
        <Link
          to="/mobile"
          className="inline-block px-4 py-2 rounded-ids-md bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 transition-colors"
        >
          Przejdź do aplikacji mobilnej
        </Link>
      </div>
    </div>
  );
}
const MobileDashboardPage = () => <FeatureInPreparation name="LexShift Mobile" />;
const MobileLoginPage = () => <FeatureInPreparation name="LexShift Mobile — logowanie" />;
const MobileSchedulePage = () => <FeatureInPreparation name="LexShift Mobile — grafik" />;
const MobileProfilePage = () => <FeatureInPreparation name="LexShift Mobile — profil" />;
const MobileLeavesPage = () => <FeatureInPreparation name="LexShift Mobile — urlopy" />;
const MobileReportsPage = () => <FeatureInPreparation name="LexShift Mobile — raporty" />;

// Kiosk layout
const KioskLayout = () => <Outlet />;

/**
 * Dynamic module page — loads partner/marketplace modules by slug from DB.
 * Resolves the module's deploy_url and renders it in an iframe.
 *
 * Gdy modułu nie da się tu otworzyć, obraz znaku mówi, CO SIĘ STAŁO —
 * według symboliki (docs/marka/ZNAK-W-SYSTEMIE.md §1), a nie jednym
 * wariantem dla wszystkiego:
 *
 * | powód                                           | wariant          | akcje                          |
 * | ----------------------------------------------- | ---------------- | ------------------------------ |
 * | pusty wynik (`PGRST116`) albo wiersz bez adresu | `nie-znaleziono` | pulpit                         |
 * | brak odpowiedzi (sieć, `status: 0`)             | `polaczenie`     | ponowienie + pulpit            |
 * | każdy inny błąd zapytania                       | `blad`           | ponowienie + pulpit            |
 * | wtyczka integracyjna                            | `brak-dostepu`*  | katalog (bez katalogu: pulpit) |
 *
 * Do rundy 7 wszystkie cztery stały na `organ-niedostepny` z nadtytułem
 * „Organ niedostępny”, a „nie znaleziono” i błąd miały samo „Spróbuj
 * ponownie”: przy `PGRST116` ponowienie zadaje to samo pytanie i dostaje tę
 * samą odpowiedź, a trasa `/admin/modules/:slug` nie ma nad sobą paska
 * powłoki — wyjściem był wstecz przeglądarki. Każdy ekran ma więc wyjście na
 * pulpit (`/admin`; tę trasę strzeże `ProtectedRoute requiredPanel="admin"`,
 * więc pulpit istnieje), a ponowienie stoi tylko tam, gdzie błąd bywa
 * przejściowy.
 *
 * (*) Wtyczka działa — nie ma tylko ekranu. Znak jest więc cały i spokojny
 * (wygląd `brak-dostepu`: znak w spoczynku), z własnym nadtytułem
 * „Integracja”; uśpiony organ z nadtytułem „Organ niedostępny” mówił
 * „awaria” obok zdania „działa w tle”. Ekran prowadzi do katalogu, gdzie
 * wtyczki leżą — a w fazie bez katalogu na pulpit, bo strażnik tras i tak by
 * tam odesłał (jak `ModuleDisabledFallback`).
 */
type NiedostepnoscModulu =
  | { rodzaj: 'integracja'; nazwa: string }
  | { rodzaj: 'brak' }
  | { rodzaj: 'polaczenie' }
  | { rodzaj: 'blad'; kod?: string };

/** Zapytanie o wiersz modułu — do szczegółów technicznych ekranu błędu. */
const SCIEZKA_MODULU = '/rest/v1/modules';

function DynamicModulePage() {
  const { slug } = useParams<{ slug: string }>();
  const { user, currentOrganization } = useAuth();
  const [moduleData, setModuleData] = React.useState<{
    name: string;
    deploy_url: string;
  } | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<NiedostepnoscModulu | null>(null);
  // „Spróbuj ponownie" zadaje to samo pytanie jeszcze raz.
  const [proba, setProba] = React.useState(0);

  React.useEffect(() => {
    if (!slug) return;

    const loadModule = async () => {
      try {
        const {
          data,
          error: dbError,
          status,
        } = await supabase
          .from('modules')
          .select('name, deploy_url, module_kind')
          .eq('id', slug)
          .eq('status', 'active')
          .single();

        // Wtyczka integracyjna (450) nie ma aplikacji do osadzenia. Wejście tu
        // z zakładki albo starego linku to nie jest „nie znaleziono" — wiersz
        // istnieje i działa, tylko nie ma ekranu. Komunikat musi to powiedzieć,
        // bo inaczej najemca szuka awarii tam, gdzie jej nie ma.
        if (!dbError && data && !moduleMaWlasnyEkran(data.module_kind)) {
          setError({ rodzaj: 'integracja', nazwa: data.name });
          return;
        }

        // „Nie ma takiego czynnego modułu" to WYŁĄCZNIE pusty wynik
        // (`PGRST116` z `.single()` — także wiersz nieaktywny albo niewidoczny
        // przez RLS) albo wiersz bez adresu. Każdy inny błąd to „nie udało się
        // załadować": zdanie „Sprawdź adres…" byłoby wtedy nieprawdą. Brak
        // sieci supabase-js oddaje jako `error` z kodem `''` i `status: 0`
        // (odpowiedź nie przyszła), a nie wyjątkiem — to `polaczenie`.
        if (dbError && dbError.code !== 'PGRST116') {
          const bezOdpowiedzi =
            status === 0 || /^(TypeError|FetchError)\b/.test(dbError.message ?? '');
          setError(
            bezOdpowiedzi
              ? { rodzaj: 'polaczenie' }
              : { rodzaj: 'blad', kod: dbError.code || (status ? String(status) : undefined) }
          );
          return;
        }

        if (dbError || !data?.deploy_url) {
          setError({ rodzaj: 'brak' });
          return;
        }

        setModuleData({ name: data.name, deploy_url: data.deploy_url });
      } catch (err) {
        // `fetch` odrzuca obietnicę `TypeError`-em, gdy odpowiedź nie przyszła.
        setError(
          err instanceof TypeError
            ? { rodzaj: 'polaczenie' }
            : { rodzaj: 'blad', kod: err instanceof Error ? err.name : undefined }
        );
      } finally {
        setLoading(false);
      }
    };

    loadModule();
  }, [slug, proba]);

  if (loading) return <ModuleLoadingFallback name={slug || 'Module'} />;

  if (error || !moduleData) {
    const pulpit = (klasa: 'btn--primary' | 'btn--secondary') => (
      <Link to="/admin" className={`btn ${klasa} btn--lg`}>
        Przejdź do pulpitu
      </Link>
    );
    const ponowIPulpit = (
      <>
        <button
          type="button"
          className="btn btn--primary btn--lg"
          onClick={() => {
            setError(null);
            setLoading(true);
            setProba((p) => p + 1);
          }}
        >
          Spróbuj ponownie
        </button>
        {pulpit('btn--secondary')}
      </>
    );
    if (error?.rodzaj === 'integracja') {
      const bezKatalogu = ukryjSprzedazSamoobslugowa(currentOrganization?.organization, user);
      return (
        <EkranBledu
          wariant="brak-dostepu"
          uklad="pelny"
          nadtytul="Integracja"
          tytul={`${error.nazwa} działa w tle`}
          tresc={
            <p>
              {bezKatalogu
                ? 'Ta integracja nie ma własnego ekranu.'
                : 'Ta integracja nie ma własnego ekranu — znajdziesz ją w katalogu modułów, na półce „Wtyczki i integracje”.'}
            </p>
          }
          akcje={
            bezKatalogu ? (
              pulpit('btn--primary')
            ) : (
              <Link to="/admin/modules" className="btn btn--primary btn--lg">
                Przejdź do modułów
              </Link>
            )
          }
        />
      );
    }
    if (error?.rodzaj === 'polaczenie') {
      return (
        <EkranBledu
          wariant="polaczenie"
          uklad="pelny"
          // Nadtytuł wariantu („Brak połączenia") i jego treść („Połączymy się
          // automatycznie") zakładałyby przyczynę i ponawianie, których nie ma —
          // wiemy tylko, że odpowiedź nie przyszła.
          nadtytul="Brak odpowiedzi"
          tytul="Nie udało się załadować modułu"
          tresc={<p>Nie dostaliśmy odpowiedzi z serwera. Sprawdź połączenie i spróbuj ponownie.</p>}
          akcje={ponowIPulpit}
          szczegoly={{ sciezka: SCIEZKA_MODULU }}
        />
      );
    }
    if (error?.rodzaj === 'blad') {
      return (
        <EkranBledu
          wariant="blad"
          uklad="pelny"
          tytul="Nie udało się załadować modułu"
          akcje={ponowIPulpit}
          szczegoly={{ kod: error.kod, sciezka: SCIEZKA_MODULU }}
        />
      );
    }
    // Nadtytuł bez numeru: `PGRST116` przychodzi z HTTP 406, a wiersz bez
    // `deploy_url` w ogóle nie jest błędem HTTP — „Błąd 404” z wariantu byłby
    // nieprawdą. Obraz znaku `nie-znaleziono` zostaje (jeden organ zabłądził).
    return (
      <EkranBledu
        wariant="nie-znaleziono"
        uklad="pelny"
        nadtytul="Nie znaleziono"
        tytul="Moduł nie został znaleziony lub jest nieaktywny"
        tresc={<p>Sprawdź adres albo poproś administratora o włączenie modułu.</p>}
        akcje={pulpit('btn--primary')}
      />
    );
  }

  return (
    <DynamicModuleFrame
      moduleSlug={slug!}
      deployUrl={moduleData.deploy_url}
      moduleName={moduleData.name}
      path="/"
    />
  );
}

// Get app mode based on domain
const appMode = getAppMode();

/**
 * Tenant root: a pure-partner account (partner role only, no tenant-admin org)
 * that logs in on the main domain used to land in the tenant-admin context with
 * no link to /partner anywhere — it could never discover its own panel.
 */
function TenantRootLanding() {
  const { user, organizations, partnerOrgId } = useAuth();
  // Konto partnerskie = same organizacje partnera (właściciel z rolą `partner`
  // ALBO pracownik zespołu w organizacji partnera, wskazanej przez partnerOrgId).
  // Bez gałęzi partnerOrgId zaproszony pracownik lądował w panelu admina najemcy
  // swojej organizacji partnerskiej — która najemcą nie jest.
  const isPartnerOnly =
    !user?.is_super_admin &&
    organizations.length > 0 &&
    organizations.every((o) => o.role === 'partner' || o.organization_id === partnerOrgId);
  if (isPartnerOnly) return <Navigate to="/partner" replace />;
  return <EarthLandingPage />;
}

// Root redirect component based on domain
function RootRedirect() {
  if (appMode.isPartnerDomain) {
    // Partner domain: redirect to Partner landing page with globe
    return (
      <ProtectedRoute requiredPanel="partner">
        <PartnerLandingPage />
      </ProtectedRoute>
    );
  }
  if (appMode.isSuperAdminDomain) {
    // Super Admin domain: redirect to Super Admin landing page with globe
    return (
      <ProtectedRoute requiredPanel="super-admin">
        <SuperAdminLandingPage />
      </ProtectedRoute>
    );
  }
  // Default: Earth Landing Page for tenant admin (or /partner for pure partners)
  return (
    <ProtectedRoute requiredPanel="admin">
      <TenantRootLanding />
    </ProtectedRoute>
  );
}

// Register redirect component based on domain
// On partner domain, show partner registration form
// On other domains, show tenant registration form
function RegisterRedirect() {
  if (appMode.isPartnerDomain) {
    return <PartnerRegisterPage />;
  }
  return <RegisterPage />;
}

/**
 * Alias adresu, który system JUŻ WYDAŁ na zewnątrz (mail, Stripe,
 * dokumentacja partnera), a pod którym powłoka nie ma własnego ekranu.
 *
 * Do 05.10.2026 takie adresy łapało `path="*"` i po cichu odsyłało na `/`,
 * więc link „Zobacz swoją taryfę prowizji" otwierał globus, a nikt nie
 * zgłaszał, że jest zły. Od kiedy `*` jest stroną 404, każdy wydany adres
 * musi mieć tu trasę — pilnuje tego `tests/unit/apps/trasyPowloki.test.ts`,
 * który czyta producentów adresów z ich plików i kładzie je na te trasy.
 *
 * Zapytanie i kotwica jadą dalej: Stripe dokleja `?session_id=…` do adresu
 * powrotu, a goły `<Navigate to="…">` by je zgubił.
 */
function Alias({ cel }: { cel: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: cel, search, hash }} replace />;
}

function App() {
  return (
    // Korzeniowe czekanie na fragment kodu to ten sam ekran startu powłoki
    // (`EkranStartuPowloki`), co czekanie na sesję w `ProtectedRoute`
    // i wstawka startowa w `index.html` — trzy etapy startu składają się
    // w jeden nieruchomy obraz, zamiast podmieniać znak na „Loading module…"
    // w połowie. Moduły w ramkach mają własne czekanie
    // (`ModuleLoadingFallback`) i to zostaje. Trasy wewnątrz paneli (admin,
    // partner, super-admin) tu nie trafiają: każdy z tych layoutów ma własną
    // granicę wokół `<Outlet />` (`EkranStartowy uklad="w-tresci"`), więc
    // pierwsze wejście w leniwy kawałek w trakcie sesji nie zdejmuje paska
    // bocznego.
    <Suspense fallback={<EkranStartuPowloki />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<LoginPage />} />
        {/* Kod 2FA po haśle — poza ProtectedRoute, bo sam jest bramką
            (lib/dwuskladnikowe.ts: `dokadPrzedTrasa` odsyła tu każdą sesję
            aal1 konta ze zweryfikowanym czynnikiem). */}
        <Route path="/2fa" element={<DwuskladnikowePage />} />
        <Route path="/register" element={<RegisterRedirect />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/register/partner" element={<PartnerRegisterPage />} />
        <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
        <Route path="/unauthorized" element={<UnauthorizedPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        {/* Oferta partnera — tozsamosc niesie token z linku, nie sesja (482). */}
        <Route path="/oferta/:token" element={<OfertaPublicznaPage />} />

        {/* Onboarding Route */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <NexusOnboardingPage />
            </ProtectedRoute>
          }
        />

        {/* Root route - redirects based on domain */}
        <Route path="/" element={<RootRedirect />} />

        {/* Admin Panel Routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requiredPanel="admin">
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="living-network" element={<LivingNetworkPage />} />
          <Route path="jarvis" element={<JarvisDashboardPage />} />
          {/* Marketplace i Billing — samoobsługowa sprzedaż, ukryta w fazie 1
            soft startu (organizacje klientów Receptora na planie free). */}
          <Route
            path="modules"
            element={
              <StrazSprzedazySamoobslugowej>
                <ModulesPage />
              </StrazSprzedazySamoobslugowej>
            }
          />
          <Route
            path="marketplace"
            element={
              <StrazSprzedazySamoobslugowej>
                <Navigate to="/admin/modules" replace />
              </StrazSprzedazySamoobslugowej>
            }
          />
          {/* Wtyczki (Saldeo, Fakturownia…) leżą na półce „Wtyczki i integracje"
            w Marketplace — osobnego ekranu nie ma. Adres wydaje mail o
            niewysłanej ewidencji kadrowej (`pocztaZdarzen/mapa.ts`). */}
          <Route path="wtyczki" element={<Alias cel="/admin/modules" />} />
          {/* Akademii Cyfrowych Zawodów TU JUŻ NIE MA. Katalog zawodów i rejestr
            zgód są ekranami organu ARTIFEX (`/academy`, `/consents`
            w `apps/module-artifex`) i wchodzi się do nich przez moduł, tak jak
            do każdego innego. Powłoka nie trzyma własnej kopii ani trasy —
            jedyne, co po Akademii zostaje po tej stronie, to wpis ARTIFEKSA
            w `modules/registry.ts` z jego `iframeRoutes`. */}
          <Route path="sites" element={<SitesPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="workers" element={<WorkersPage />} />
          {/* Kartoteka pracownika. Ma własny adres, a nie modal, bo musi dać
            się podesłać, otworzyć w nowej karcie i wskazać z wnętrza modułu —
            moduł odsyła tu przez postMessage `navigate`. */}
          <Route path="workers/:workerId" element={<WorkerDossierPage />} />
          <Route path="devices" element={<DevicesPage />} />
          {/* Centralny organ druku — kartoteka infrastrukturalna powłoki
            (plan PLAN_REALIZACJI_ORGAN_DRUKU §7). Zakładki żyją w ?tab=. */}
          <Route path="printing" element={<PrintingPage />} />
          {/* Obieg dokumentów (plan 53 §11.2, D53-35) — zakładki w ?tab=. Ekran pracy, nie
            sprzedaży: BEZ StrazSprzedazySamoobslugowej (plan 53 §11.1). */}
          <Route path="obieg" element={<ObiegDokumentowPage />} />
          <Route
            path="billing"
            element={
              <StrazSprzedazySamoobslugowej>
                <BillingPage />
              </StrazSprzedazySamoobslugowej>
            }
          />
          <Route path="products" element={<AdminProductsPage />} />
          <Route path="categories" element={<AdminCategoriesPage />} />
          <Route path="suppliers" element={<AdminSuppliersPage />} />
          <Route path="equipment" element={<AdminEquipmentPage />} />
          <Route path="technicians" element={<AdminTechniciansPage />} />
          <Route path="company-profile" element={<CompanyProfilePage />} />
          <Route path="support" element={<SupportPage />} />
          {/* Moje premie mieszkają w PANELU PARTNERA — premie z prowizji
            partnera, nie funkcja tenanta. Stary adres przekierowuje, żeby
            zakładki nie umarły. */}
          <Route path="premie" element={<Navigate to="/partner/premie" replace />} />
          {/* Akademia Universe UŻYTKOWNIKA najemcy (13.09.2026, plan §4.7):
            ten sam program co Akademia partnera, bez rozdziałów partnerskich,
            postęp per osoba (`odbiorca: 'uzytkownik'`, migracja 691).
            Wcześniej przekierowanie do /partner/akademia kończyło się odmową
            „brak profilu partnera" dla każdego najemcy. */}
          <Route path="akademia" element={<AkademiaUzytkownikaPage />} />
          <Route path="settings" element={<SettingsPage />} />
          {/* Legacy alias — kanoniczny panel NEXUS tenanta żyje pod
            /admin/modules/nexus (pages/admin/nexus/*, realne API). */}
          <Route path="nexus" element={<Navigate to="/admin/modules/nexus" replace />} />
        </Route>

        {/* Built-in modules — routes generated from the module registry */}
        {MODULE_REGISTRY.filter((m) => !m.native).map((m) => (
          <Route
            key={m.slug}
            path={`/admin/modules/${m.slug}`}
            element={
              <ProtectedRoute requiredPanel="admin">
                <ModuleLayout />
              </ProtectedRoute>
            }
          >
            {(m.iframeRoutes ?? []).map((r) =>
              r.route === 'index' ? (
                <Route
                  key="index"
                  index
                  element={<BuiltInModuleRoute slug={m.slug} path={r.path} />}
                />
              ) : (
                <Route
                  key={r.route}
                  path={r.route}
                  element={<BuiltInModuleRoute slug={m.slug} path={r.path} />}
                />
              )
            )}
          </Route>
        ))}

        {/* NEXUS Module - sidebar layout with sub-routes */}
        <Route
          path="/admin/modules/nexus"
          element={
            <ProtectedRoute requiredPanel="admin">
              <NexusModuleLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<NexusOverviewPage />} />
          <Route path="channels" element={<NexusChannelsPage />} />
          <Route path="billing" element={<NexusBillingPage />} />
          <Route path="settings" element={<NexusSettingsPage />} />
        </Route>

        {/* UCS Module — fetches a one-time SSO ticket before mounting the iframe.
          Must stay ABOVE the dynamic :slug catch-all. */}
        <Route
          path="/admin/modules/ucs"
          element={
            <ProtectedRoute requiredPanel="admin">
              <UcsModulePage />
            </ProtectedRoute>
          }
        />

        {/* Dynamic Partner/Marketplace Modules — catch-all for DB-registered modules */}
        <Route
          path="/admin/modules/:slug/*"
          element={
            <ProtectedRoute requiredPanel="admin">
              <DynamicModulePage />
            </ProtectedRoute>
          }
        />

        {/* Legacy alias — the landing with the globe is canonical at '/' on the
          super-admin domain; keep old bookmarks working via redirect. */}
        <Route path="/super-admin-home" element={<Navigate to="/" replace />} />

        {/* Super Admin Panel */}
        <Route
          path="/super-admin"
          element={
            <ProtectedRoute requiredPanel="super-admin">
              <SuperAdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<SuperAdminDashboard />} />
          <Route path="tenants" element={<TenantsPage />} />
          <Route path="tenant-analytics" element={<TenantAnalyticsPage />} />
          <Route path="licenses" element={<LicensesPage />} />
          <Route path="partners" element={<PartnersPage />} />
          <Route path="partner-settlements" element={<PartnerSettlementsPage />} />
          <Route path="partner-mailboxes" element={<PartnerMailboxesPage />} />
          {/* Kartoteka certyfikatów partnerskich: certyfikat otwiera wejście do
            organu na koncie klienta (717), więc unieważnienie musi mieć ekran,
            a nie tylko funkcję w bazie. */}
          <Route path="partner-certyfikaty" element={<PartnerCertyfikatyPage />} />
          <Route path="klienci-receptora" element={<KlienciReceptoraPage />} />
          <Route path="pairings" element={<PairingsPage />} />
          <Route path="flota-paneli" element={<FlotaPaneliPage />} />
          <Route path="simulations" element={<SimulationsPage />} />
          <Route path="module-review" element={<ModuleReviewPage />} />
          <Route path="module-pricing" element={<ModulePricingPage />} />
          <Route path="katalog-sprzetu" element={<KatalogSprzetuPage />} />
          <Route path="karty-sprzedazowe" element={<KartySprzedazowePage />} />
          <Route path="plan-pricing" element={<PlanPricingPage />} />
          <Route path="mobile-pricing" element={<MobilePricingPage />} />
          <Route path="subscriptions" element={<SubscriptionsPage />} />
          <Route path="email-config" element={<EmailConfigPage />} />
          <Route path="email-templates" element={<EmailTemplatesPage />} />
          <Route path="nexus" element={<NexusSuperAdminPage />} />
          <Route path="cockpit" element={<CockpitPage />} />
          <Route path="super-nexus" element={<SuperNexusPage />} />
          <Route path="living-network" element={<PlatformLivingNetworkPage />} />
          <Route path="nexus-proposals" element={<NexusProposalsPage />} />
          <Route path="code-tasks" element={<CodeTasksPage />} />
          <Route path="genesis-connectors" element={<GenesisConnectorsPage />} />
          <Route path="nexus-assistant" element={<NexusAssistantDashboardPage />} />
          <Route path="nexus-assistant-updates" element={<NexusAssistantUpdatesPage />} />
          <Route path="omnivoice-updates" element={<OmniVoiceUpdatesPage />} />
          <Route path="service-health" element={<ServiceHealthPage />} />
          <Route path="health-incidents" element={<HealthIncidentsPage />} />
          <Route path="steering-opportunities" element={<SteeringOpportunitiesPage />} />
          <Route path="modules" element={<ModulesManagementPage />} />
          <Route path="audit-log" element={<AuditLogPage />} />
          <Route path="support" element={<SupportPage />} />
          <Route path="settings" element={<SuperAdminSettingsPage />} />
        </Route>

        {/* Legacy alias — the partner landing is canonical at '/' on the partner
          domain; keep old bookmarks working via redirect. */}
        <Route path="/partner-home" element={<Navigate to="/" replace />} />

        {/* Podgląd oferty partnera (897) — ta sama strona co `/oferta/:token`,
          bez PartnerLayout, bo ma wyglądać dokładnie tak, jak zobaczy ją klient.
          Dostęp rozstrzyga baza (`partner_oferta_podglad`). */}
        <Route
          path="/partner/oferty/:id/podglad"
          element={
            <ProtectedRoute requiredPanel="partner">
              <PartnerOfertaPodgladPage />
            </ProtectedRoute>
          }
        />

        {/* Partner Panel */}
        <Route
          path="/partner"
          element={
            <ProtectedRoute requiredPanel="partner">
              <PartnerLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<PartnerDashboard />} />
          <Route path="clients" element={<PartnerClientsPage />} />
          <Route path="analytics" element={<PartnerClientAnalyticsPage />} />
          <Route path="commissions" element={<PartnerCommissionsPage />} />
          <Route path="pricing" element={<PartnerOfferPricingPage />} />
          <Route path="territory" element={<PartnerTerritoryPage />} />
          <Route path="reports" element={<PartnerReportsPage />} />
          <Route path="team" element={<PartnerTeamPage />} />
          <Route path="profile" element={<PartnerProfilePage />} />
          {/* Taryfa i próg prowizji są sekcją profilu (`StatusProgramuPartnera`).
            Adres wydają maile o awansie i spadku progu (`prowizje/progi.ts`). */}
          <Route path="taryfa" element={<Alias cel="/partner/profile" />} />
          <Route path="modules" element={<PartnerModulesPage />} />
          {/* Zgłoszenie modułu to okno na liście modułów, nie osobna strona.
            Adres podaje przewodnik partnera (`docs/03-API/PARTNER_MODULE_GUIDE.md`). */}
          <Route path="modules/submit" element={<Alias cel="/partner/modules" />} />
          {/* Akademia Nexusa — pełnoekranowe szkolenie produktowe (renderuje
            się nad layoutem przez fixed inset-0; trasa siedzi pod /partner,
            żeby dziedziczyć ProtectedRoute i kontekst partnera). */}
          <Route path="akademia" element={<AkademiaPage />} />
          {/* Certyfikaty modułowe — kurs, teoria, praktyka i nagroda w jednym
            miejscu (fale P5–P7 planu 45). Certyfikat jest warunkiem wejścia
            partnera do organu na koncie KLIENTA. */}
          <Route path="certyfikaty" element={<CertyfikatyPage />} />
          {/* Moje premie — premie/udział z prowizji partnera i oceny wiedzy
            CZŁONKA ZESPOŁU partnera (RLS oddaje wyłącznie własne wiersze). */}
          <Route path="premie" element={<MojePremiePage />} />
          <Route path="support" element={<SupportPage />} />
          <Route path="settings" element={<PartnerSettingsPage />} />
        </Route>

        {/* LexShift Mobile PWA - Employee self-service app.
          MaintenanceGate blocks these PIN/anon shop-floor surfaces (which sit
          outside ProtectedRoute) while global maintenance mode is on. */}
        <Route
          path="/mobile/lexshift"
          element={
            <MaintenanceGate>
              <MobileLayout />
            </MaintenanceGate>
          }
        >
          <Route index element={<MobileDashboardPage />} />
          <Route path="login" element={<MobileLoginPage />} />
          <Route path="schedule" element={<MobileSchedulePage />} />
          <Route path="profile" element={<MobileProfilePage />} />
          <Route path="leaves" element={<MobileLeavesPage />} />
          <Route path="reports" element={<MobileReportsPage />} />
        </Route>

        {/* LexShift Kiosk - Employee clock in/out terminal */}
        <Route
          path="/kiosk/lexshift"
          element={
            <MaintenanceGate>
              <KioskLayout />
            </MaintenanceGate>
          }
        >
          {/* Bez sesji terminal idzie do parowania, nie do logowania
              administratora — terminal na hali nie ma konta (bramka:
              tests/unit/apps/kioskBezSesji.test.tsx). */}
          <Route
            index
            element={
              <BuiltInModuleRoute
                slug="lexshift"
                path="/kiosk"
                celBezSesji="/kiosk/lexshift/activate"
              />
            }
          />
          {/* Parowanie terminala kodem 6-cyfrowym. Wcześniej stał tu wyłącznie
              spinner (`ModuleLoadingFallback`), mimo że kompletny ekran
              aktywacji istniał w pages/mobile/kiosk/KioskApp.tsx i nie był
              podpięty do żadnej trasy. `KioskApp` sam decyduje: brak sparowania
              → ekran aktywacji, sparowany → terminal RCP. */}
          <Route path="activate" element={<KioskApp />} />
        </Route>

        {/* Mobile App - Universal mobile application with activation */}
        {/* After activation, shows menu with available modules based on license */}
        <Route
          path="/mobile/*"
          element={
            <MaintenanceGate>
              <MobileApp />
            </MaintenanceGate>
          }
        />
        <Route
          path="/m/*"
          element={
            <MaintenanceGate>
              <MobileApp />
            </MaintenanceGate>
          }
        />

        {/* User-friendly short URLs - all redirect to unified mobile app */}
        <Route path="/app" element={<Navigate to="/mobile" replace />} />
        <Route path="/kiosk" element={<Navigate to="/kiosk/lexshift" replace />} />

        {/* Adresy wydane na zewnątrz poza drzewami `/admin` i `/partner`
          (alias jest w drzewie, gdy adres w nim leży). Producenci:
          - `/settings/billing` — powrót z portalu Stripe (`hooks/useBilling.ts`)
            i domyślne `BILLING_RETURN_URL` w `neurosense-core/api/routes/billing.ts`,
            także z `?session_id=…` po płatności;
          - `/settings/notifications` — stopka „wypisz się" maili
            (`packages/email-templates/src/templates/{hr,operations,reports}.ts`);
            osobnego ekranu preferencji powiadomień jeszcze nie ma, więc
            prowadzi do ustawień organizacji;
          - `/neurosense` — przycisk „Otwórz pulpit NeuroSense" w podsumowaniu
            dobowym (`templates/reports.ts`). */}
        <Route path="/settings/billing" element={<Alias cel="/admin/billing" />} />
        <Route path="/settings/notifications" element={<Alias cel="/admin/settings" />} />
        <Route path="/neurosense" element={<Alias cel="/admin/modules/neurosense" />} />

        {/* Nie znaleziono — ekran 404, a NIE ciche przekierowanie na `/`
          (patrz `NieZnalezionoPage`). Wydane adresy bez ekranu mają aliasy
          wyżej; `trasyPowloki.test.ts` nie wpuści nowego producenta adresu,
          który by tu trafił. */}
        <Route path="*" element={<NieZnalezionoPage />} />
      </Routes>
    </Suspense>
  );
}

export default App;
